import axios from 'axios';
import isEmpty from 'lodash/isEmpty';
import findIndex from 'lodash/findIndex';
import moment from 'moment';

// constants
import { API_URL_NEW } from '/src/lib/endpoints';
import { applyAmountSuffix } from '/src/lib/helpers';
import { FUNDING_STATUS, TAXONOMIES, DIRECTORY_TYPE } from '/src/lib/constants';
import { dfhqBooleanFilters, highPotentialFilters } from '../constants/filterConfigStartup';

// helpers
import { prepareSelectParam, prepareFilterParam } from '/src/lib/queryParams';
import * as DirectoryHelpers from './directoryHelpers';
import * as filterHelper from '/src/lib/filterHelper';

// api
import entityApi from '/src/apis/entityApi';
import fundApi from '/src/apis/fundApi';

// get startups for directory
// getStartups' 8 default amount suffixes, in parameter order
export const DEFAULT_AMOUNT_SUFFIXES = Array(8).fill('000');

export const getStartups = (
    page,
    limit,
    isPageChanged,
    filter,
    user,
    newFilters,
    selectableFields,
    totalAmountMinSuffix = '000',
    totalAmountMaxSuffix = '000',
    amountraisedMinSuffix = '000',
    amountraisedMaxSuffix = '000',
    valuationMinSuffix = '000',
    valuationMaxSuffix = '000',
    exitAmountMinSuffix = '000',
    exitAmountMaxSuffix = '000',
    signal
) => {

    let data = {};
    const tempFilter = { ...filter };
    const sort = tempFilter.order && tempFilter.order.field
        ? `${tempFilter.order.field}^:${tempFilter.order.order}`
        : 'last_date_raised^:desc'; // if no sort is specified

    // If page hasn't changed, set it to 1
    // This will help when page was set to > 1 and filters were applied
    if (!isPageChanged) {
        page = 1;
    }

    // add filters, sort, etc.
    newFilters = prepNewFilters(
        tempFilter,
        newFilters,
        totalAmountMinSuffix,
        totalAmountMaxSuffix,
        amountraisedMinSuffix,
        amountraisedMaxSuffix,
        valuationMinSuffix,
        valuationMaxSuffix,
        exitAmountMinSuffix,
        exitAmountMaxSuffix
    );
    newFilters = newFilters.finalFilters;

    if (newFilters) {
        // make search saveable if any filters are applied
        if (user.is_loggedin) {
            data.isSearchSaveable = true;
        }
    } else {
        data.isSearchSaveable = false;
    }

    // update the list of selected fields/columns
    selectableFields = DirectoryHelpers.handleSelectableFieldsUpdate(newFilters, selectableFields);

    // prepare list of default fields, then add the selectable columns that the user chose
    let dataFields = ['id', 'name', 'code', 'owned_by', 'logo_data', 'number_of_rounds_raised'];
    selectableFields.map((field) => {
        if (field.checked && field.allowAccess) {
            dataFields.push(field.keyName);
        }
    })

    const fields = prepareSelectParam([...new Set(dataFields)]);

    const URL = `${API_URL_NEW}/startup/?fields=${fields}&filters=${newFilters}&limit=${limit}&page=${page}&sort=${sort}`;
    return new Promise((resolve, reject) => {
        axios.get(URL, { signal }).then(response => {
            if (response.data.status === 200) {
                let startups = response.data.data;
                for (let startup of startups) {
                    if (startup.owned_by && startup.owned_by > 0) {
                        startup.checked = false;
                    }
                }

                data.page = page;
                data.data = startups;
                data.count = response.data.navigation.total;
            }
            resolve(data)
        }).catch(err => reject(err))
    })
}

// prep new filters
export const prepNewFilters = (
    oldFilters,
    newFilters,
    totalAmountMinSuffix = '000',
    totalAmountMaxSuffix = '000',
    amountraisedMinSuffix = '000',
    amountraisedMaxSuffix = '000',
    valuationMinSuffix = '000',
    valuationMaxSuffix = '000',
    exitAmountMinSuffix = '000',
    exitAmountMaxSuffix = '000'
) => {

    let arFilters = [];
    Object.keys(oldFilters).map((key) => {
        if (key === 'keyword' && oldFilters[key].length > 0) {
            newFilters.keyword.value = oldFilters[key];
            arFilters.push(newFilters.keyword);
        }

        else if (key === 'investor' && oldFilters[key].length > 0) {
            newFilters.investor.value = [oldFilters[key][0].id];
            newFilters.investor_type.value = [oldFilters[key][0].ecosystem_type];

            arFilters.push(newFilters.investor);
            arFilters.push(newFilters.investor_type);
        }

        else if (key === 'acquirer' && oldFilters[key].length > 0) {
            newFilters.acquirer.value = [oldFilters[key][0].id];
            newFilters.acquirer_type.value = [oldFilters[key][0].ecosystem_type];

            arFilters.push(newFilters.acquirer);
            arFilters.push(newFilters.acquirer_type);
        }

        else if (key === 'program' && oldFilters[key].length > 0) {
            newFilters.program.value = oldFilters[key].map((v) => v.id);
            newFilters.program_client_dir.value = [oldFilters[key][0].client_directory_id];

            arFilters.push(newFilters.program);
            arFilters.push(newFilters.program_client_dir);
        }

        else if (key === 'primary_industry' && oldFilters[key].length > 0) {

            const primaryIndustriesIds = [];
            const subIndustriesIds = [];

            oldFilters[key].forEach((filter) => {
                if (filter.parent_id === 0) {
                    primaryIndustriesIds.push(filter.id);
                } else {
                    subIndustriesIds.push(filter.id);
                }
            });

            newFilters.primary_industry.value = primaryIndustriesIds;
            newFilters.sub_industry.value = subIndustriesIds;

            arFilters.push(newFilters.primary_industry);
            arFilters.push(newFilters.sub_industry);
        }

        else if (key === 'entity_relation' && oldFilters[key].length > 0) {
            newFilters.entity_rel_filter.value = [oldFilters[key][0].id];
            newFilters.entity_rel_type_filter.value = [oldFilters[key][0].type];

            arFilters.push(newFilters.entity_rel_filter);
            arFilters.push(newFilters.entity_rel_type_filter);
        }

        else if (key === 'employee' && !isEmpty(oldFilters[key])) {
            newFilters.max_num_emp.value = [oldFilters[key]['max'].toString()];
            newFilters.min_num_emp.value = [oldFilters[key]['min'].toString()];
            arFilters.push(newFilters.min_num_emp, newFilters.max_num_emp);
        }

        else if (key === 'date' && !isEmpty(oldFilters[key])) {
            newFilters.date_founded.value = [
                `${oldFilters[key]['from']}-01-01`,
                `${oldFilters[key]['to']}-12-31`
            ];
            arFilters.push(
                newFilters.date_founded,
            );
        }

        else if (key === 'totalamount' && !isEmpty(oldFilters[key])) {
            const minVal = oldFilters[key].min
                ? applyAmountSuffix(oldFilters[key]['min'], totalAmountMinSuffix)
                : '';
            const maxVal = oldFilters[key].max
                ? applyAmountSuffix(oldFilters[key]['max'], totalAmountMaxSuffix)
                : '';

            if (minVal && maxVal) {
                newFilters.total_fund.value = [minVal, maxVal];
                arFilters.push(newFilters.total_fund);
            } else if (minVal) {
                newFilters.total_fund_from.value = [minVal];
                arFilters.push(newFilters.total_fund_from);
            } else if (maxVal) {
                newFilters.total_fund_to.value = [maxVal];
                arFilters.push(newFilters.total_fund_to);
            }
        }

        else if (key === 'amountraised' && !isEmpty(oldFilters[key])) {
            const minVal = oldFilters[key].min
                ? applyAmountSuffix(oldFilters[key]['min'], amountraisedMinSuffix)
                : '';
            const maxVal = oldFilters[key].max
                ? applyAmountSuffix(oldFilters[key]['max'], amountraisedMaxSuffix)
                : '';

            if (minVal && maxVal) {
                newFilters.amount_raised_filter.value = [minVal, maxVal];
                arFilters.push(newFilters.amount_raised_filter);
            } else if (minVal) {
                newFilters.amount_raised_filter_from.value = [minVal];
                arFilters.push(newFilters.amount_raised_filter_from);
            } else if (maxVal) {
                newFilters.amount_raised_filter_to.value = [maxVal];
                arFilters.push(newFilters.amount_raised_filter_to);
            }
        }

        else if (key === 'valuation' && !isEmpty(oldFilters[key])) {
            const minVal = oldFilters[key].min
                ? applyAmountSuffix(oldFilters[key].min, valuationMinSuffix)
                : '';
            const maxVal = oldFilters[key].max
                ? applyAmountSuffix(oldFilters[key].max, valuationMaxSuffix)
                : '';

            if (minVal && maxVal) {
                newFilters.valuation_filter.value = [minVal, maxVal];
                arFilters.push(newFilters.valuation_filter);
            } else if (minVal) {
                newFilters.valuation_filter_from.value = [minVal];
                arFilters.push(newFilters.valuation_filter_from);
            } else if (maxVal) {
                newFilters.valuation_filter_to.value = [maxVal];
                arFilters.push(newFilters.valuation_filter_to);
            }
        }

        else if (key === 'exitAmount' && !isEmpty(oldFilters[key])) {
            const minVal = oldFilters[key].min
                ? applyAmountSuffix(oldFilters[key].min, exitAmountMinSuffix)
                : '';
            const maxVal = oldFilters[key].max
                ? applyAmountSuffix(oldFilters[key].max, exitAmountMaxSuffix)
                : '';

            if (minVal && maxVal) {
                newFilters.exit_amount_filter.value = [minVal, maxVal];
                arFilters.push(newFilters.exit_amount_filter);
            } else if (minVal) {
                newFilters.exit_amount_filter_from.value = [minVal];
                arFilters.push(newFilters.exit_amount_filter_from);
            } else if (maxVal) {
                newFilters.exit_amount_filter_to.value = [maxVal];
                arFilters.push(newFilters.exit_amount_filter_to);
            }
        }

        else if (key === 'investment_date' && oldFilters[key]) {
            const tempFromValue = oldFilters[key].from ? oldFilters[key].from : new Date(0);
            const fromVal = `${tempFromValue.getFullYear()}-${tempFromValue.getMonth() + 1}-${tempFromValue.getDate()}`;

            const tempToValue = oldFilters[key].to ? oldFilters[key].to : new Date();
            const toVal = `${tempToValue.getFullYear()}-${tempToValue.getMonth() + 1}-${tempToValue.getDate()}`;

            newFilters.investment_date_from.value = [fromVal];
            newFilters.investment_date_to.value = [toVal];

            arFilters.push(newFilters.investment_date_from, newFilters.investment_date_to);
        }

        else if (key === 'exit_date' && oldFilters[key]) {
            const tempFromValue = oldFilters[key].from ? oldFilters[key].from : new Date(0);
            const fromVal = `${tempFromValue.getFullYear()}-${tempFromValue.getMonth() + 1}-${tempFromValue.getDate()}`;

            const tempToValue = oldFilters[key].to ? oldFilters[key].to : new Date();
            const toVal = `${tempToValue.getFullYear()}-${tempToValue.getMonth() + 1}-${tempToValue.getDate()}`;

            newFilters.exit_date_from.value = [fromVal];
            newFilters.exit_date_to.value = [toVal];

            arFilters.push(newFilters.exit_date_from, newFilters.exit_date_to);
        }

        // last date raised filter
        else if (key === 'last_date_raised' && oldFilters[key]) {
            const fromVal = oldFilters[key].from
                ? `${oldFilters[key]['from'].getFullYear()}-${oldFilters[key]['from'].getMonth() + 1}-${oldFilters[key]['from'].getDate()}`
                : '';
            const toVal = oldFilters[key].to
                ? `${oldFilters[key]['to'].getFullYear()}-${oldFilters[key]['to'].getMonth() + 1}-${oldFilters[key]['to'].getDate()}`
                : '';

            if (fromVal && toVal) {
                newFilters.last_date_raised.value = [fromVal, toVal];
                arFilters.push(newFilters.last_date_raised);
            } else {
                if (fromVal) {
                    newFilters.last_date_raised_from.value = [fromVal];
                    arFilters.push(newFilters.last_date_raised_from);
                }

                if (toVal) {
                    newFilters.last_date_raised_to.value = [toVal];
                    arFilters.push(newFilters.last_date_raised_to);
                }

            }
        }

        // boolean filters
        // When selected, send 1 as a value
        else if (key === 'is_funded' && oldFilters[key]) {
            newFilters[key].value = oldFilters[key] == 'Funded' ? [1] : [0];
            arFilters.push(newFilters[key]);
        } else if (key === 'revenue' && oldFilters[key]) {
            newFilters[key].value = oldFilters[key] == 'yes' ? [1] : [0];
            arFilters.push(newFilters[key]);
        } else if (key === 'active' && oldFilters[key]) {
            newFilters[key].value = oldFilters[key] == 'active' ? [1] : [0];
            arFilters.push(newFilters[key]);
        } else if (key === 'gender' && oldFilters[key]) {
            newFilters[key].value = [oldFilters[key]];
            arFilters.push(newFilters[key]);
        } else if (key === 'funding_verification_status' && oldFilters[key]) {
            newFilters[key].value = oldFilters[key] ? [FUNDING_STATUS.verified] : [];
            arFilters.push(newFilters[key]);
        } else if (dfhqBooleanFilters.includes(key) && oldFilters[key]) {
            newFilters[key].value = [1];
            arFilters.push(newFilters[key]);
        } else if (key === 'lookbackMonths') {
            const hasHighPotentialFilter = highPotentialFilters.some(f => Boolean(oldFilters[f]));
            if (hasHighPotentialFilter && oldFilters[key]) {
                newFilters[key].value = [oldFilters[key]];
                arFilters.push(newFilters[key]);
            }
        } else if (key === 'region' || key === 'investorRegion' || key === 'acquirerRegion' || key === 'investmentRegion') {
            return;
            
        } else {
            if (newFilters[key] && oldFilters[key] && oldFilters[key].length > 0) {
                if (Array.isArray(oldFilters[key])) {
                    newFilters[key].value = oldFilters[key].map((v) => v.id);
                    arFilters.push(newFilters[key]);
                } else {
                    newFilters[key].value = [oldFilters[key]];
                    arFilters.push(newFilters[key]);
                }
            }
        }
    });

    let finalFilters = prepareFilterParam(arFilters);
    if (!finalFilters) {
        finalFilters = '';
    }

    let data = {};
    data.finalFilters = finalFilters;
    return data;
}

// prep new filters
export const prepNewFiltersFunding = (
    oldFilters,
    newFilters,
    totalAmountMinSuffix = '000',
    totalAmountMaxSuffix = '000',
    amountraisedMinSuffix = '000',
    amountraisedMaxSuffix = '000',
    valuationMinSuffix = '000',
    valuationMaxSuffix = '000',
    exitAmountMinSuffix = '000',
    exitAmountMaxSuffix = '000'
) => {

    let arFilters = [];
    Object.keys(oldFilters).map((key) => {

        if (key === 'keyword' && oldFilters[key].length > 0) {
            newFilters.keyword.value = oldFilters[key];
            arFilters.push(newFilters.keyword);
        }

        else if (key === 'investor' && oldFilters[key].length > 0) {
            newFilters.investor.value = [oldFilters[key][0].id];
            newFilters.investor_type.value = [oldFilters[key][0].ecosystem_type];

            arFilters.push(newFilters.investor);
            arFilters.push(newFilters.investor_type);
        }

        else if (key === 'acquirer' && oldFilters[key].length > 0) {
            newFilters.acquirer.value = [oldFilters[key][0].id];
            newFilters.acquirer_type.value = [oldFilters[key][0].ecosystem_type];

            arFilters.push(newFilters.acquirer);
            arFilters.push(newFilters.acquirer_type);
        }

        else if (key === 'program' && oldFilters[key].length > 0) {
            newFilters.program.value = oldFilters[key].map((v) => v.id);
            newFilters.program_client_dir.value = [oldFilters[key][0].client_directory_id];

            arFilters.push(newFilters.program);
            arFilters.push(newFilters.program_client_dir);
        }

        else if (key === 'primary_industry' && oldFilters[key].length > 0) {

            const primaryIndustriesIds = [];
            const subIndustriesIds = [];

            oldFilters[key].forEach((filter) => {
                if (filter.parent_id === 0) {
                    primaryIndustriesIds.push(filter.id);
                } else {
                    subIndustriesIds.push(filter.id);
                }
            });

            newFilters.primary_industry.value = primaryIndustriesIds;
            newFilters.sub_industry.value = subIndustriesIds;

            arFilters.push(newFilters.primary_industry);
            arFilters.push(newFilters.sub_industry);
        }

        else if (key === 'entity_relation' && oldFilters[key].length > 0) {
            newFilters.entity_rel_filter.value = [oldFilters[key][0].id];
            newFilters.entity_rel_type_filter.value = [oldFilters[key][0].type];

            arFilters.push(newFilters.entity_rel_filter);
            arFilters.push(newFilters.entity_rel_type_filter);
        }

        else if (key === 'totalamount' && !isEmpty(oldFilters[key])) {
            const minVal = oldFilters[key].min
                ? applyAmountSuffix(oldFilters[key]['min'], totalAmountMinSuffix)
                : '';
            const maxVal = oldFilters[key].max
                ? applyAmountSuffix(oldFilters[key]['max'], totalAmountMaxSuffix)
                : '';

            if (minVal && maxVal) {
                newFilters.total_fund.value = [minVal, maxVal];
                arFilters.push(newFilters.total_fund);
            } else if (minVal) {
                newFilters.total_fund_from.value = [minVal];
                arFilters.push(newFilters.total_fund_from);
            } else if (maxVal) {
                newFilters.total_fund_to.value = [maxVal];
                arFilters.push(newFilters.total_fund_to);
            }
        }

        else if (key === 'amountraised' && !isEmpty(oldFilters[key])) {
            const minVal = oldFilters[key].min
                ? applyAmountSuffix(oldFilters[key]['min'], amountraisedMinSuffix)
                : '';
            const maxVal = oldFilters[key].max
                ? applyAmountSuffix(oldFilters[key]['max'], amountraisedMaxSuffix)
                : '';

            if (minVal && maxVal) {
                newFilters.amount_raised_filter.value = [minVal, maxVal];
                arFilters.push(newFilters.amount_raised_filter);
            } else if (minVal) {
                newFilters.amount_raised_filter_from.value = [minVal];
                arFilters.push(newFilters.amount_raised_filter_from);
            } else if (maxVal) {
                newFilters.amount_raised_filter_to.value = [maxVal];
                arFilters.push(newFilters.amount_raised_filter_to);
            }
        }

        else if (key === 'valuation' && !isEmpty(oldFilters[key])) {
            const minVal = oldFilters[key].min
                ? applyAmountSuffix(oldFilters[key].min, valuationMinSuffix)
                : '';
            const maxVal = oldFilters[key].max
                ? applyAmountSuffix(oldFilters[key].max, valuationMaxSuffix)
                : '';

            if (minVal && maxVal) {
                newFilters.valuation_filter.value = [minVal, maxVal];
                arFilters.push(newFilters.valuation_filter);
            } else if (minVal) {
                newFilters.valuation_filter_from.value = [minVal];
                arFilters.push(newFilters.valuation_filter_from);
            } else if (maxVal) {
                newFilters.valuation_filter_to.value = [maxVal];
                arFilters.push(newFilters.valuation_filter_to);
            }
        }

        else if (key === 'exitAmount' && !isEmpty(oldFilters[key])) {
            const minVal = oldFilters[key].min
                ? applyAmountSuffix(oldFilters[key].min, exitAmountMinSuffix)
                : '';
            const maxVal = oldFilters[key].max
                ? applyAmountSuffix(oldFilters[key].max, exitAmountMaxSuffix)
                : '';

            if (minVal && maxVal) {
                newFilters.exit_amount_filter.value = [minVal, maxVal];
                arFilters.push(newFilters.exit_amount_filter);
            } else if (minVal) {
                newFilters.exit_amount_filter_from.value = [minVal];
                arFilters.push(newFilters.exit_amount_filter_from);
            } else if (maxVal) {
                newFilters.exit_amount_filter_to.value = [maxVal];
                arFilters.push(newFilters.exit_amount_filter_to);
            }
        }

        else if (key === 'investment_date' && oldFilters[key]) {
            const tempFromValue = oldFilters[key].from ? oldFilters[key].from : new Date(0);
            const fromVal = `${tempFromValue.getFullYear()}-${tempFromValue.getMonth() + 1}-${tempFromValue.getDate()}`;

            const tempToValue = oldFilters[key].to ? oldFilters[key].to : new Date();
            const toVal = `${tempToValue.getFullYear()}-${tempToValue.getMonth() + 1}-${tempToValue.getDate()}`;

            newFilters.investment_date_from.value = [fromVal];
            newFilters.investment_date_to.value = [toVal];

            arFilters.push(newFilters.investment_date_from, newFilters.investment_date_to);
        }

        else if (key === 'exit_date' && oldFilters[key]) {
            const tempFromValue = oldFilters[key].from ? oldFilters[key].from : new Date(0);
            const fromVal = `${tempFromValue.getFullYear()}-${tempFromValue.getMonth() + 1}-${tempFromValue.getDate()}`;

            const tempToValue = oldFilters[key].to ? oldFilters[key].to : new Date();
            const toVal = `${tempToValue.getFullYear()}-${tempToValue.getMonth() + 1}-${tempToValue.getDate()}`;

            newFilters.exit_date_from.value = [fromVal];
            newFilters.exit_date_to.value = [toVal];

            arFilters.push(newFilters.exit_date_from, newFilters.exit_date_to);
        }

        // last date raised filter
        else if (key === 'last_date_raised' && oldFilters[key]) {
            const fromVal = oldFilters[key].from
                ? `${oldFilters[key]['from'].getFullYear()}-${oldFilters[key]['from'].getMonth() + 1}-${oldFilters[key]['from'].getDate()}`
                : '';
            const toVal = oldFilters[key].to
                ? `${oldFilters[key]['to'].getFullYear()}-${oldFilters[key]['to'].getMonth() + 1}-${oldFilters[key]['to'].getDate()}`
                : '';

            if (fromVal && toVal) {
                newFilters.last_date_raised.value = [fromVal, toVal];
                arFilters.push(newFilters.last_date_raised);
            } else {
                if (fromVal) {
                    newFilters.last_date_raised_from.value = [fromVal];
                    arFilters.push(newFilters.last_date_raised_from);
                }

                if (toVal) {
                    newFilters.last_date_raised_to.value = [toVal];
                    arFilters.push(newFilters.last_date_raised_to);
                }

            }
        }

        else if (key === 'funding_verification_status' && oldFilters[key]) {
            newFilters[key].value = oldFilters[key] ? [FUNDING_STATUS.verified] : [];
            arFilters.push(newFilters[key]);
        }

        else if (dfhqBooleanFilters.includes(key) && oldFilters[key]) {
            newFilters[key].value = [1];
            arFilters.push(newFilters[key]);
        }

        else if (key === 'lookbackMonths') {
            if (highPotentialFilters.some(f => Boolean(oldFilters[f])) && oldFilters[key]) {
                newFilters[key].value = [oldFilters[key]];
                arFilters.push(newFilters[key]);
            }
        }

        else if (key === 'date' && !isEmpty(oldFilters[key])) {
            newFilters.date_founded.value = [
                `${oldFilters[key]['from']}-01-01`,
                `${oldFilters[key]['to']}-12-31`
            ];
            arFilters.push(
                newFilters.date_founded,
            );
        }

        else if (key === 'active' && oldFilters[key]) {
            newFilters[key].value = oldFilters[key] == 'active' ? [1] : [0];
            arFilters.push(newFilters[key]);

        }
        
        else if (key === 'employee' && !isEmpty(oldFilters[key])) {
            newFilters.investedin_max_num_emp_filter.value = [oldFilters[key]['max'].toString()];
            newFilters.investedin_min_num_emp_filter.value = [oldFilters[key]['min'].toString()];
            arFilters.push(newFilters.investedin_max_num_emp_filter, newFilters.investedin_min_num_emp_filter);
        }

        else if (key === 'region' || key === 'investorRegion' || key === 'acquirerRegion' || key === 'investmentRegion') {
            return;
        }

        else {
            if (newFilters[key] && oldFilters[key] && oldFilters[key].length > 0) {
                if (Array.isArray(oldFilters[key])) {
                    newFilters[key].value = oldFilters[key].map((v) => v.id);
                    arFilters.push(newFilters[key]);
                } else {
                    newFilters[key].value = [oldFilters[key]];
                    arFilters.push(newFilters[key]);
                }
            }
        }

    });

    let finalFilters = prepareFilterParam(arFilters);
    if (!finalFilters) {
        finalFilters = '';
    }

    let data = {};
    data.finalFilters = finalFilters;
    return data;
}

// getSavedFundsName Gets the list of saved filters names to show selected filters
const getSavedFundsName = async (filterArr) => {
    let filterObj = filterArr.find(item => item.hasOwnProperty('fund_filter'))

    let list = []
    const selectedFunds = filterObj?.fund_filter || [];
    if (selectedFunds.length > 0) {
        const hasOthersFund = selectedFunds.some((fundId) => fundId === null || String(fundId).toLowerCase() === 'null');
        const selectedNamedFundIds = selectedFunds.filter(
            (fundId) => fundId !== null && String(fundId).toLowerCase() !== 'null' && String(fundId).trim() !== ''
        );

        if (selectedNamedFundIds.length > 0) {
            const data = await fundApi.getFundsById(selectedNamedFundIds)
            list = data;
        }

        if (hasOthersFund) {
            list.push({ id: "null", name: 'Others' });
        }
    }

    return list
}

// Hydrate a raw entity ID (from a saved-search URL) into a {id, name, ...extraFields}
// filter chip, degrading gracefully to a nameless chip if the lookup fails.
const resolveEntityFilter = async (id, entityType, extraFields = {}) => {
    if (!id) return [];

    const obj = { id, name: '', ...extraFields };

    try {
        const objData = await entityApi.getEntityName(id, entityType);
        if (!isEmpty(objData)) {
            obj.name = objData.name;
        }
    } catch (err) {
        console.error(err);
    }

    return [obj];
};

export const handleSavedFilters = async (filterArr, stateObj, savedCitiesName) => {

    const { 
        selectableFields, 
        locations, 
        primary_industrys, 
        stages, 
        regions, 
        investmentVehicles, 
        investorRegions, 
        investorLocations, 
        investorTaxonomyTypes, 
        acquirerRegions,
        acquirerLocations,
        acquirerTypes,
        investmentRegions,
        investmentLocations,
        companys 
    } = stateObj;

    // prepare list of default fields, then add the selectable columns that the user chose
    let dataFields = ['id', 'name', 'code', 'owned_by', 'logo_data'];
    selectableFields.map((field) => {
        if (field.checked && field.allowAccess) {
            dataFields.push(field.keyName);
        }
    })

    const fields  = prepareSelectParam(dataFields);
    let extraStateFields = {};
    let employee  = '';

    let locationFilter = filterHelper.getFilterByKey(
        filterArr,
        'hq_id',
        locations
    );

    let cityFilter = filterHelper.getFilterByKey(
        filterArr,
        'city_id',
        savedCitiesName
    );

    let industryFilter = filterHelper.getFilterByKey(
        filterArr,
        'industry',
        primary_industrys
    );

    let stageFilters = filterHelper.getFilterByKey(
        filterArr,
        'development_stage',
        stages
    );

    const fundingStageFilters = filterHelper.getFilterByKey(
        filterArr,
        'funding_stage',
        stateObj.funding_stages
    );

    const exitTypeFilters = filterHelper.getFilterByKey(
        filterArr,
        'exit_type_filter',
        stateObj.exitTypes
    );

    const lastStageRaisedFilters = filterHelper.getFilterByKey(
        filterArr,
        'last_stage_filter',
        stateObj.last_stage_raiseds
    );

    let keywords = filterHelper.getFilterByKey(
        filterArr, 
        'keyword', 
        [], 
        true
    );

    let active = filterHelper.getFilterByKey(
        filterArr, 
        'is_active', 
        [], 
        true
    );

    let funded = filterHelper.getFilterByKey(
        filterArr,
        'is_funded',
        [],
        true
    );

    let date = filterHelper.getRangeFilterByKey(
        filterArr, 
        'date_founded'
    );

    let maxEmp = filterHelper.getFilterByKey(
        filterArr,
        'max_num_emp',
        [],
        true
    );

    let minEmp = filterHelper.getFilterByKey(
        filterArr,
        'min_num_emp',
        [],
        true
    );

    let totalFund = filterHelper.getRangeFilterByKey(
        filterArr, 
        'total_fund'
    );

    let businessType = filterHelper.getFilterByKey(
        filterArr,
        'business_type',
        stateObj.types
    );

    const regionFilter = filterHelper.getFilterByKey(
        filterArr, 
        'region', 
        regions
    );

    const primaryIndustryFilter = filterHelper.getFilterByKey(
        filterArr, 
        'primary_industry_filter', 
        primary_industrys
    );

    const technologyFilter = filterHelper.getFilterByKey(
        filterArr,
        'technology_filter',
        stateObj.technologys
    );

    const fundFilter = await getSavedFundsName(filterArr);

    const subIndustryFilter = filterHelper.getFilterByKey(
        filterArr, 
        'sub_industry_filter', 
        primary_industrys
    );

    const secondaryIndustryFilter = filterHelper.getFilterByKey(
        filterArr, 
        'sec_industry_filter', 
        primary_industrys
    );

    const genderArr = filterHelper.getFilterByKey(
        filterArr, 
        'gender_filter', 
        [], 
        true
    );
    const gender = genderArr && genderArr.length > 0 ? genderArr[0] : '';
    extraStateFields.gender = gender;

    let investmentDate = filterHelper.getRangeFilterByKey(
        filterArr,
        'investment_date_filter'
    );

    let exitDate = filterHelper.getRangeFilterByKey(
        filterArr,
        'exit_date_filter'
    );

    let lastDateRaised = filterHelper.getRangeFilterByKey(
        filterArr, 
        'last_date_raised'
    );

    const numberOfRoundsRaisedArr = filterHelper.getFilterByKey(
        filterArr, 
        'number_of_rounds_raised', 
        [], 
        true
    );
    const numberOfRoundsRaised = numberOfRoundsRaisedArr && numberOfRoundsRaisedArr.length > 0 ? numberOfRoundsRaisedArr[0] : '';

    const fundingVerificationStatus = filterHelper.getFilterByKey(
        filterArr, 
        'funding_verification_status_filter', 
        [], 
        true
    );

    const investorFilter = filterHelper.getFilterByKey(
        filterArr,
        'investor_filter',
        [],
        true
    );

    const investorTypeFilter = filterHelper.getFilterByKey(
        filterArr, 
        'investor_type_filter', 
        [], 
        true
    );


    const acquirerFilter = filterHelper.getFilterByKey(
        filterArr,
        'acquirer_filter',
        [],
        true
    );

    const acquirerTypeFilter = filterHelper.getFilterByKey(
        filterArr,
        'acquirer_type_filter',
        [],
        true
    );

    let investmentVehicleFilter = filterHelper.getFilterByKey(
        filterArr, 
        'investment_vehicle_filter', 
        investmentVehicles
    );

    let amountRaisedFilter = filterHelper.getRangeFilterByKey(
        filterArr, 
        'amount_raised_filter'
    );

    let valuationFilter = filterHelper.getRangeFilterByKey(
        filterArr,
        'valuation_filter'
    );

    let exitAmountFilter = filterHelper.getRangeFilterByKey(
        filterArr,
        'exit_amount_filter'
    );

    const investorLocationFilter = filterHelper.getFilterByKey(
        filterArr,
        'investor_country_filter',
        investorLocations
    );

    const investorRegionsFilter = filterHelper.getFilterByKey(
        filterArr, 
        'investor_region_filter', 
        investorRegions
    );

    const investorTaxonomyTypeFilter  = filterHelper.getFilterByKey(
        filterArr,
        'investor_type_id_filter',
        investorTaxonomyTypes
    );

    const acquirerLocationFilter = filterHelper.getFilterByKey(
        filterArr,
        'acquirer_country_filter',
        acquirerLocations
    );

    const acquirerRegionsFilter = filterHelper.getFilterByKey(
        filterArr,
        'acquirer_region_filter',
        acquirerRegions
    );

    const acquirerTaxonomyTypeFilter = filterHelper.getFilterByKey(
        filterArr,
        'acquirer_type_id_filter',
        acquirerTypes
    );

    const companyFilter = filterHelper.getFilterByKey(
        filterArr,
        'id',
        [],
        true
    );

    const investmentLocationFilter = filterHelper.getFilterByKey(
        filterArr,
        'investment_country_id_filter',
        investmentLocations
    );

    const investmentRegionsFilter = filterHelper.getFilterByKey(
        filterArr, 
        'investment_region_id_filter', 
        investmentRegions
    );

    // handle the old industry filter saved data
    if (industryFilter && industryFilter.length > 0) {
        industryFilter.forEach(ind => {
            const isPrimaryInd = findIndex(primary_industrys, { id: ind.id });

            if (isPrimaryInd > -1) {
                primaryIndustryFilter.push(ind);
                secondaryIndustryFilter.push(ind);
            } else {
                subIndustryFilter.push(ind);
            }
        })
    }

    totalFund = filterHelper.restoreAmountRange(totalFund, 'total_fund');
    amountRaisedFilter = filterHelper.restoreAmountRange(amountRaisedFilter, 'amount_raised_filter');
    valuationFilter = filterHelper.restoreAmountRange(valuationFilter, 'valuation_filter');
    exitAmountFilter = filterHelper.restoreAmountRange(exitAmountFilter, 'exit_amount_filter');

    if (minEmp && maxEmp) {
        employee = { min: parseInt(minEmp), max: parseInt(maxEmp) };
        extraStateFields.employee_maxValue = employee.max;
        extraStateFields.employee_minValue = employee.min;
    }

    if (date && date.min && date.max) {
        date.min = (date.min && date.min.date_founded) ? date.min.date_founded : date.min;
        date.max = (date.max && date.max.date_founded) ? date.max.date_founded : date.max;

        extraStateFields.date_minValue = parseInt(moment(date.min).format('YYYY'));
        extraStateFields.date_maxValue = parseInt(moment(date.max).format('YYYY'));

        // Accomodate old date founded filters
        date.from = extraStateFields.date_minValue;
        date.to = extraStateFields.date_maxValue
    }

    if (active && active.length > 0 && active[0] == '1') {
        active = 'active';
    } else if (active && active.length > 0 && active[0] == '0') {
        active = 'inactive';
    } else {
        active = '';
    }

    if (funded && funded.length > 0 && funded[0] == '1') {
        funded = 'Funded';
    } else if (funded && funded.length > 0 && funded[0] == '0') {
        funded = 'Not Funded';
    } else {
        funded = '';
    }

    // investment date filter
    if (investmentDate) {
        let fromDate, toDate;

        if (investmentDate.min.investment_date_filter || investmentDate.max.investment_date_filter) {
            fromDate = investmentDate.min && investmentDate.min.investment_date_filter ? new Date(investmentDate.min.investment_date_filter) : "";
            toDate = investmentDate.max && investmentDate.max.investment_date_filter ? new Date(investmentDate.max.investment_date_filter) : "";
        } else {
            fromDate = investmentDate.min ? new Date(investmentDate.min) : "";
            toDate = investmentDate.max ? new Date(investmentDate.max) : "";
        }

        investmentDate = { from: fromDate, to: toDate };
    }

    // exit date filter
    if (exitDate) {
        let fromDate, toDate;

        if (exitDate.min?.exit_date_filter || exitDate.max?.exit_date_filter) {
            fromDate = exitDate.min && exitDate.min.exit_date_filter ? new Date(exitDate.min.exit_date_filter) : "";
            toDate = exitDate.max && exitDate.max.exit_date_filter ? new Date(exitDate.max.exit_date_filter) : "";
        } else {
            fromDate = exitDate.min ? new Date(exitDate.min) : "";
            toDate = exitDate.max ? new Date(exitDate.max) : "";
        }

        exitDate = { from: fromDate, to: toDate };
    }

    // last date raised filter
    if (lastDateRaised) {
        let fromDate, toDate;
        if (lastDateRaised.min?.last_date_raised || lastDateRaised.max.last_date_raised) {
            fromDate = lastDateRaised.min && lastDateRaised.min.last_date_raised ? new Date(lastDateRaised.min.last_date_raised) : "";
            toDate = lastDateRaised.max && lastDateRaised.max.last_date_raised ? new Date(lastDateRaised.max.last_date_raised) : "";
        } else {
            fromDate = lastDateRaised.min ? new Date(lastDateRaised.min) : "";
            toDate = lastDateRaised.max ? new Date(lastDateRaised.max) : "";
        }

        lastDateRaised = { from: fromDate, to: toDate };
    }

    const [arrInvestorFilter, arrAcquirerFilter, arrCompanyFilter] = await Promise.all([
        (investorFilter && investorTypeFilter)
            ? resolveEntityFilter(investorFilter[0], investorTypeFilter[0], { ecosystem_type: investorTypeFilter[0] })
            : [],
        (acquirerFilter && acquirerTypeFilter)
            ? resolveEntityFilter(acquirerFilter[0], acquirerTypeFilter[0], { ecosystem_type: acquirerTypeFilter[0] })
            : [],
        (companyFilter && companyFilter.length > 0)
            ? resolveEntityFilter(companyFilter[0], DIRECTORY_TYPE.startup)
            : [],
    ]);

    let tempFilter = {
        keyword: keywords != undefined ? keywords : [],
        investor: arrInvestorFilter,
        acquirer: arrAcquirerFilter,
        region: regionFilter,
        location: locationFilter,
        city: cityFilter,
        primary_industry: [...primaryIndustryFilter, ...subIndustryFilter],
        technology: technologyFilter,
        fund: fundFilter,
        sec_industry: secondaryIndustryFilter,
        stage: stageFilters,
        funding_stage: fundingStageFilters || [],
        exitType: exitTypeFilters || [],
        last_stage_raised: lastStageRaisedFilters || [],
        type: businessType,
        date: date && date.from && date.to ? date : '',
        employee: employee,
        active,
        is_funded: funded ? funded : '',
        totalamount: !isEmpty(totalFund) ? totalFund : '',
        order: { field: "last_date_raised", order: "desc" },
        gender: gender,
        number_of_rounds_raised: numberOfRoundsRaised,
        investment_date: investmentDate ? investmentDate : '',
        exit_date: exitDate ? exitDate : '',
        last_date_raised: lastDateRaised ? lastDateRaised : '',
        funding_verification_status: fundingVerificationStatus,
        amountraised: !isEmpty(amountRaisedFilter) ? amountRaisedFilter : '',
        valuation: !isEmpty(valuationFilter) ? valuationFilter : '',
        exitAmount: !isEmpty(exitAmountFilter) ? exitAmountFilter : '',
        investmentVehicle: investmentVehicleFilter,
        investorRegion: investorRegionsFilter,
        investorLocation: investorLocationFilter,
        investorTaxonomyType: investorTaxonomyTypeFilter,
        acquirerRegion: acquirerRegionsFilter,
        acquirerLocation: acquirerLocationFilter,
        acquirerType: acquirerTaxonomyTypeFilter,
        company: arrCompanyFilter,
        investmentRegion: investmentRegionsFilter,
        investmentLocation: investmentLocationFilter
    };

    return {
        tempFilter,
        extraStateFields
    }
}